import React from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCard from '@mui/icons-material/CreditCard';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import { ROLE_CUSTOM_OFFER_ADMIN_USER } from '~/consts/roles';

import PermissionedComponent from '../../PermissionedComponent';

interface Props {
  customOfferStatus: App.CustomOffer['status'];
  onCreateVCC: () => Promise<void>;
  isLoading: boolean;
  validationErrors: Array<string> | null;
  reservationError: string | null;
}

export function VCCForCustomOfferPreConditions({
  customOfferStatus,
  onCreateVCC,
  validationErrors,
  reservationError,
  isLoading,
}: Props) {
  const isStatusApproved = customOfferStatus === 'approved';

  const conditions = [
    {
      label: 'The custom offer status must be approved.',
      isMet: isStatusApproved,
    },
  ];

  const allConditionsMet = conditions.every((condition) => condition.isMet);

  return (
    <Box
      sx={{
        py: 1,
        px: 2,
        bgcolor: 'grey.50',
        borderRadius: 1,
      }}
    >
      <Stack spacing={1} mt={1}>
        {conditions.map((condition, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {condition.isMet ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
            <Typography>{condition.label}</Typography>
          </Box>
        ))}

        <Typography variant="caption" color="warning.main" sx={{ mb: 2, display: 'block' }}>
          Please verify the Vendor Currency, Vendor Cost Price, and payment dates before proceeding. <br /> These
          details cannot be modified after the virtual credit card (VCC) has been created.
        </Typography>

        {validationErrors && validationErrors.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.1, mb: 2 }}>
            {validationErrors.map((error, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CancelIcon color="error" sx={{ fontSize: '16px' }} />
                <Typography color="error" variant="body1">
                  {error}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {reservationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {reservationError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <PermissionedComponent
            requiredRoles={[ROLE_CUSTOM_OFFER_ADMIN_USER]}
            fallback={
              <Button
                variant="contained"
                color="primary"
                size="medium"
                disabled
                sx={{ mt: 1 }}
                startIcon={<ReportGmailerrorredIcon />}
                data-testid="vcc-no-permissions-button"
              >
                No Permissions
              </Button>
            }
          >
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={onCreateVCC}
              disabled={!allConditionsMet || isLoading}
              sx={{ mt: 1 }}
              startIcon={<CreditCard />}
              data-testid="vcc-create-button"
            >
              Create Virtual Credit Card
            </Button>
          </PermissionedComponent>
        </Box>
      </Stack>
    </Box>
  );
}
